/** Espelho de conducco-admin-backend/src/modules/configuration-inspection/contracts/types.ts. Mesmos nomes. */
export type Lens = "C" | "J" | "E" | "T" | "$";
export type Nature = "PRESCRIPTIVE" | "DESCRIPTIVE";
export type Severity = "BLOCKER" | "MAJOR" | "MINOR";
export type Section = "ORGANIZATION" | "HEALTH" | "DENTAL" | "LIFE";
export type ProductLine = "HEALTH" | "DENTAL" | "LIFE";
export type TargetType = "ORGANIZATION" | "ADDRESSABLE_GROUP" | "WORKSPACE";
export type Owner = "CORRETORA" | "RH" | "CONDUCCO" | "OPERADORA";
export type Block =
  | "cargos" | "conectores_organizacao" | "enderecabilidade" | "cadastro" | "cobranca_faturamento" | "operacao"
  | "elegibilidade" | "politica_beneficios" | "reajuste" | "atividades_esteira" | "configuracao_atividade"
  | "inclusao" | "transferencias";
export type FindingDiffStatus = "NEW" | "PERSISTENT" | "RESOLVED" | "NOT_EVALUATED";

export interface IFinding {
  fingerprint: string;
  checkId: string;
  section: Section;
  block: Block;
  lens: Lens;
  nature: Nature;
  severity: Severity | null;
  title: string;
  evidence: Record<string, unknown>;
  /** why: parte estável da evidência usada no fingerprint no backend; na leitura vem sempre "" (hardcoded em inspection-repository.ts). Nunca exibir na tela. */
  evidenceKey: string;
  consequence: string;
  action: string;
  owner: Owner;
  setupLink: string | null;
}

export interface ITarget {
  id: string;
  targetType: TargetType;
  targetId: string;
  productLine: ProductLine | null;
  /** why: params do alvo no momento da inspeção (regras, endereçabilidade); a tabela de diff de fieldChanges lê contra isto. */
  paramsSnapshot: Record<string, unknown>;
  /** why: check_ids que correram neste alvo; sem isso o diff não distingue "resolvido" de "não avaliado". */
  evaluatedCheckIds: string[];
  skipped: boolean;
  skipReason: string | null;
  findings: IFinding[];
}

export interface ISummary {
  bySeverity: Record<Severity, number>;
  descriptiveCount: number;
  byLens: Record<Lens, number>;
  bySection: Record<Section, number>;
  targets: number;
  skippedTargets: number;
}

export interface IInspection {
  id: string;
  sellerOrganizationId: string;
  buyerOrganizationId: string;
  startedAt: string;
  finishedAt: string;
  triggeredByUserId: string | null;
  trigger: "MANUAL";
  engineVersion: string;
  catalogVersion: string;
  summary: ISummary;
  targets: ITarget[];
}

export interface IInspectionListItem {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  triggeredByUserId: string | null;
  catalogVersion: string;
  summary: ISummary;
}

export interface IFieldChange {
  targetType: TargetType;
  targetId: string;
  /** why: grupo misto (saúde + odonto) gera dois alvos com o mesmo targetId; distingue qual mudou. */
  productLine: ProductLine | null;
  path: string;
  before: unknown;
  after: unknown;
}

export interface IEvidenceChange {
  fingerprint: string;
  changes: { path: string; before: unknown; after: unknown }[];
}

export interface IVersionChange {
  from: string;
  to: string;
}

export interface IInspectionDiff {
  previousInspectionId: string | null;
  findings: { fingerprint: string; status: FindingDiffStatus }[];
  fieldChanges: IFieldChange[];
  /** why: o fingerprint de um achado com lista não inclui a lista; a variação (1 de 14 cargos) aparece aqui, não como "resolvido + novo". */
  evidenceChanges: IEvidenceChange[];
  /** why: com catálogo ou motor diferentes, um achado que some pode ter sumido pela mudança da verificação, não da configuração. */
  catalogChanged: IVersionChange | null;
  engineChanged: IVersionChange | null;
}

export interface IInspectionResponse {
  inspection: IInspection;
  diff: IInspectionDiff;
}

export const SECTION_LABEL: Record<Section, string> = { ORGANIZATION: "CNPJ", HEALTH: "Saúde", DENTAL: "Odonto", LIFE: "Vida em Grupo" };
export const BLOCK_LABEL: Record<Block, string> = {
  cargos: "Cargos", conectores_organizacao: "Conectores da organização", enderecabilidade: "Endereçabilidade", cadastro: "Cadastro",
  cobranca_faturamento: "Cobrança e Faturamento", operacao: "Operação", elegibilidade: "Elegibilidade", politica_beneficios: "Política de benefícios",
  reajuste: "Reajuste", atividades_esteira: "Atividades e esteira", configuracao_atividade: "Configuração por atividade",
  inclusao: "Inclusão com consentimento", transferencias: "Transferências",
};
export const BLOCK_ORDER: Block[] = [
  "cargos", "conectores_organizacao", "enderecabilidade", "cadastro", "cobranca_faturamento", "operacao",
  "elegibilidade", "politica_beneficios", "reajuste", "atividades_esteira", "configuracao_atividade",
  "inclusao", "transferencias",
];
export const LENS_LABEL: Record<Lens, string> = { C: "Consequência", J: "Para conferência", E: "Quem ficou de fora", T: "Prontidão técnica", $: "Custo" };
// invariant: igual a DESCRIPTIVE_MARK de report/labels.ts do backend; a marca de um achado descritivo é sempre
// esta, nunca LENS_LABEL[lens] (um descritivo de lente E diria "Quem ficou de fora", que não é uma marca).
export const DESCRIPTIVE_MARK = "Para conferência";
export const SEVERITY_LABEL: Record<Severity, string> = { BLOCKER: "Bloqueia", MAJOR: "Importante", MINOR: "Menor" };
export const OWNER_LABEL: Record<Owner, string> = { CORRETORA: "corretora", RH: "RH da empresa", CONDUCCO: "Conducco", OPERADORA: "operadora" };
export const DIFF_LABEL: Record<FindingDiffStatus, string> = { NEW: "novo", PERSISTENT: "persiste", RESOLVED: "resolvido", NOT_EVALUATED: "não avaliado" };

export interface IInspectionReportChapter {
  buyerOrganizationId: string;
  inspectionId: string | null;
  previousInspectionId: string | null;
}

export interface IInspectionReportResponse {
  html: string;
  filename: string;
  title: string;
  chapters: IInspectionReportChapter[];
}
